// Escáner profesional (solo cámara). Al leer un QR navega a /escanear/:codigo
// para ver la invitación y marcarla. No apila resultados en esta página.
import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase/firebaseConfig';
import './invitaciones/escaner.css';

function parseInvitationId(raw) {
  try {
    const t = (raw || '').toString().trim();
    if (!t) return null;
    if (t.startsWith('{')) {
      const obj = JSON.parse(t);
      return obj.invitacionId || obj.id || obj.inv || null;
    }
    if (/^https?:\/\//i.test(t)) {
      const url = new URL(t);
      return (
        url.searchParams.get('invitacionId') ||
        url.searchParams.get('id') ||
        url.pathname.split('/').filter(Boolean).pop() ||
        null
      );
    }
    return t;
  } catch {
    return (raw || '').toString().trim();
  }
}

async function stopScannerSafely(qr) {
  if (!qr) return;
  try { await qr.stop(); } catch {}
  try { await qr.clear(); } catch {}
}

function playScanFeedback() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      const ctx = new AudioCtx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.type = 'sine';
      o.frequency.setValueAtTime(1040, ctx.currentTime);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.01);
      o.start();
      o.stop(ctx.currentTime + 0.12);
      setTimeout(() => { try { ctx.close(); } catch {} }, 250);
    }
  } catch {}

  if (navigator?.vibrate) {
    try { navigator.vibrate(40); } catch {}
  }
}

export default function ScanQR() {
  const navigate = useNavigate();

  const [logged, setLogged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authMsg, setAuthMsg] = useState('');
  const [scannerReady, setScannerReady] = useState(false);
  const [error, setError] = useState(null);

  const qrRef = useRef(null);
  const startedRef = useRef(false);
  const processingScanRef = useRef(false);
  const mountedRef = useRef(false);

  // Solo comprobamos sesión (la ruta Protected y Firestore validan el rol/permisos)
  useEffect(() => {
    mountedRef.current = true;

    const unsub = onAuthStateChanged(auth, (u) => {
      if (!mountedRef.current) return;

      if (!u) {
        setLogged(false);
        setAuthMsg('Debes iniciar sesión para usar el escáner.');
      } else {
        setLogged(true);
        setAuthMsg('');
      }
      setLoading(false);
    });

    return () => {
      mountedRef.current = false;
      unsub();
    };
  }, []);

  // Inicializar cámara (esta página SOLO muestra la cámara)
  useEffect(() => {
    if (!logged || loading || startedRef.current) return;

    const elId = 'qr-reader';
    const el = document.getElementById(elId);
    if (!el) return;

    // Evita residuos del canvas/video si el usuario vuelve rápido al escáner.
    el.innerHTML = '';
    setError(null);
    setScannerReady(false);
    processingScanRef.current = false;

    const qr = new Html5Qrcode(elId);
    qrRef.current = qr;

    const onScanSuccess = (decodedText) => {
      // html5-qrcode puede disparar varias lecturas del mismo QR en milisegundos.
      // Este bloqueo evita múltiples navigate/stop simultáneos, que en móviles congela la vista.
      if (processingScanRef.current) return;
      processingScanRef.current = true;

      const id = parseInvitationId(decodedText);
      if (!id) {
        processingScanRef.current = false;
        setError('QR no reconocido');
        return;
      }

      playScanFeedback();

      // Pausamos de inmediato y navegamos. No esperamos qr.stop(), porque en móviles
      // a veces se queda esperando y parece que el escáner se colgó.
      try { qr.pause(true); } catch {}
      startedRef.current = false;
      navigate(`/escanear/${encodeURIComponent(id)}`);
    };

    const onScanFailure = () => {}; // ignoramos fallos intermedios

    qr.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 280, height: 280 }, aspectRatio: 1 },
      onScanSuccess,
      onScanFailure
    ).then(() => {
      if (!mountedRef.current) return;
      startedRef.current = true;
      setScannerReady(true);
    }).catch((e) => {
      console.error(e);
      if (!mountedRef.current) return;
      setError('No se pudo iniciar la cámara. Revisa permisos.');
      startedRef.current = false;
      processingScanRef.current = false;
    });

    // Al salir de esta página, apagamos la cámara en segundo plano.
    return () => {
      const currentQr = qrRef.current;
      qrRef.current = null;
      startedRef.current = false;
      processingScanRef.current = false;
      setScannerReady(false);
      stopScannerSafely(currentQr);
    };
  }, [logged, loading, navigate]);

  // ===== UI =====
  if (loading) {
    return (
      <div className="scan-layout">
        <div className="scan-card"><div className="scan-title">Preparando cámara…</div></div>
      </div>
    );
  }

  if (!logged) {
    return (
      <div className="scan-layout">
        <div className="scan-card">
          <div className="scan-title">Acceso restringido</div>
          <p className="scan-msg">{authMsg || 'Inicia sesión para continuar.'}</p>
          <button className="btn ghost" onClick={() => navigate('/login')}>Iniciar sesión</button>
        </div>
      </div>
    );
  }

  return (
    <div className="scan-layout">
      <header className="scan-header">
        <div className="brand-dot" />
        <span>Palmarama · Escáner de Invitaciones</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button
            className="btn ghost"
            onClick={() => {
              const el = document.documentElement;
              if (!document.fullscreenElement) el.requestFullscreen?.(); else document.exitFullscreen?.();
            }}
          >
            Pantalla completa
          </button>
        </div>
      </header>

      <section className="scan-camera">
        <div className="scan-instructions">
          <h2>Escanear QR</h2>
          <p>{scannerReady ? 'Apunta y centra el código en el marco.' : 'Iniciando cámara…'}</p>
        </div>

        <div className="qr-reader-surface">
          <div id="qr-reader" className="qr-box"></div>
          <div className="qr-frame">
            <span className="corner tl" /><span className="corner tr" />
            <span className="corner bl" /><span className="corner br" />
          </div>
        </div>

        {error && (
          <div className="scan-card" style={{ marginTop: 16 }}>
            <div className="scan-title">Error</div>
            <p className="scan-msg">{error}</p>
          </div>
        )}

        <div className="scan-tips">
          <ul>
            <li>Si está oscuro, sube el brillo del teléfono.</li>
            <li>Evita reflejos y mantén el QR estable.</li>
          </ul>
        </div>
      </section>
    </div>
  );
}
